import React, { useState, useRef, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Alert,
  LinearProgress,
  Skeleton,
  Link,
} from "@mui/material";
import { PictureAsPdf } from "@mui/icons-material";
import PdfViewerDialog from "components/common/PdfViewerDialog";
import { stripSourceCitations } from "utils/clinicalSources";
import amhLogo from "assets/images/logos/amhlearn-logo.png";
import kijabeLogo from "assets/images/logos/kijabe-logo.png";
import CustomMessageInput from "components/chat_new/CustomMessageInput";
import { ChatMessage, TypingIndicator } from "components/chat";
import { chatScrollbarSx } from "components/chat_new/chatColors";
import { invokeLLMPrompt } from "services/llmService";
import { patientLanguage } from "utils/patientLanguage";
import { retrieveClinicalReferences } from "services/clinicalReferencesService";
import { AuthContext } from "context/Auth";
import { useRateLimit } from "hooks/useRateLimit";
import { useTranslation } from "react-i18next";
import { DASHBOARD_CONTENT_HEIGHT } from "constants/layout";

const AMA_PROMPT_ID = "ask-me-anything";
const MAX_SOURCES = 4;

function mergeSourcesByUrl(embeddedDocs, clinicalRefs) {
  const byUrl = new Map();
  for (const doc of embeddedDocs || []) {
    if (!doc?.downloadURL) continue;
    byUrl.set(doc.downloadURL, {
      downloadURL: doc.downloadURL,
      fileName: doc.fileName || "",
      similarity: typeof doc.similarity === "number" ? doc.similarity : null,
    });
  }
  for (const ref of clinicalRefs || []) {
    if (!ref?.downloadURL || byUrl.has(ref.downloadURL)) continue;
    byUrl.set(ref.downloadURL, {
      downloadURL: ref.downloadURL,
      fileName: ref.fileName || "",
      similarity: null,
    });
  }
  return Array.from(byUrl.values())
    .sort((a, b) => (b.similarity ?? -1) - (a.similarity ?? -1))
    .slice(0, MAX_SOURCES);
}

// A source with no file name reads as "Document", in the reader's language;
// callers pass t("chat.document"). Display-only: nothing here is sent anywhere.
function displayLabel(source, fallback = "Document") {
  return (
    (source.fileName || fallback)
      .replace(/\.(pdf|docx?|txt|md)$/i, "")
      .replace(/[_-]/g, " ")
      .trim() || fallback
  );
}

function SourceLinks({ sources, onOpenPdf }) {
  const { t } = useTranslation("assistant");
  if (!sources?.length) return null;
  return (
    <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: "divider" }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5 }}
      >
        {t("chat.sourceDocuments")}
      </Typography>
      {sources.map((s) => (
        <Box
          key={s.downloadURL}
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}
        >
          <PictureAsPdf
            sx={{ fontSize: "0.9rem", color: "error.main", flexShrink: 0 }}
          />
          <Link
            component="button"
            variant="caption"
            onClick={() => onOpenPdf(s)}
            sx={{ textAlign: "left", wordBreak: "break-word" }}
          >
            {displayLabel(s, t("chat.document"))}
          </Link>
        </Box>
      ))}
    </Box>
  );
}

/**
 * isDashboard: true when rendered inside /dashboard (ProfessionalRoutes).
 * - Hides the usage bar and treats the user as unlimited.
 * - Uses full available viewport height.
 * WP embed path (/ask-me-anything in AppRoutes) leaves isDashboard false.
 */
const AskMeAnythingPage = ({ isDashboard = false }) => {
  const { t } = useTranslation("assistant");
  const { user, userData, subscription, organization } =
    useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const [pdfViewer, setPdfViewer] = useState({
    open: false,
    url: null,
    title: null,
  });
  const chatBoxRef = useRef(null);

  // Only compute rate limit for the WP / unauthenticated path.
  const rateLimit = useRateLimit(
    isDashboard ? null : userData,
    isDashboard ? null : subscription,
    isDashboard ? null : organization,
  );

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleOpenPdf = (source) => {
    if (source?.downloadURL) {
      setPdfViewer({
        open: true,
        url: source.downloadURL,
        title: displayLabel(source, t("chat.document")),
      });
    }
  };

  const handleSend = async (text) => {
    const trimmed = (typeof text === "string" ? text : inputValue).trim();
    if (!trimmed || isTyping || !user) return;

    setInputValue("");
    setError(null);
    setMessages((prev) => [
      ...prev,
      { role: "user", content: trimmed, sources: [] },
    ]);
    setIsTyping(true);

    try {
      const [result, clinicalRefs] = await Promise.all([
        invokeLLMPrompt({
          promptId: AMA_PROMPT_ID,
          userMessage: trimmed,
          includeEmbeddedDocuments: true,
          embeddedDocumentsOptions: { chunkLimit: 8 },
          // The public page follows the reader's language; the professional dashboard sends
          // none, so its prompt is unchanged.
          ...(!isDashboard && { language: patientLanguage() }),
        }),
        retrieveClinicalReferences(trimmed, { promptId: AMA_PROMPT_ID }).catch(
          () => [],
        ),
      ]);

      const embeddedDocs = result.embeddedDocuments?.documents ?? [];
      const sources = mergeSourcesByUrl(embeddedDocs, clinicalRefs);
      const content = stripSourceCitations(
        result.output || t("chat.noResponse"),
      );

      setMessages((prev) => [...prev, { role: "assistant", content, sources }]);
    } catch (err) {
      setError(err.message || t("askMeAnything.requestFailedTryAgain"));
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: t("chat.error", {
            message: err.message || t("chat.requestFailed"),
          }),
          isError: true,
          sources: [],
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const renderUsageBar = () => {
    if (!rateLimit) {
      return (
        <Box sx={{ mb: 2, height: 66 }}>
          <Skeleton width="60%" />
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <Skeleton
              variant="rounded"
              height={10}
              sx={{ flexGrow: 1, borderRadius: 5 }}
            />
            <Skeleton width={80} height={20} sx={{ ml: 2 }} />
          </Box>
          <Skeleton width="40%" />
        </Box>
      );
    }

    const {
      tokensUsed,
      tokenLimit,
      tokenPercentage,
      isFreeTier,
      messageCount,
      messageLimit,
      messagePercentage,
      isRateLimited,
      nextReset,
    } = rateLimit;

    const displayPct = isFreeTier
      ? Math.max(tokenPercentage, messagePercentage)
      : tokenPercentage;
    const barColor =
      displayPct >= 100 ? "error" : displayPct > 75 ? "warning" : "secondary";

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2">{t("askMeAnything.usage")}</Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <LinearProgress
            variant="determinate"
            value={displayPct}
            color={barColor}
            sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: 2, minWidth: 110, whiteSpace: "nowrap" }}
          >
            {isFreeTier
              ? t("askMeAnything.messagesUsed", {
                  used: messageCount,
                  limit: messageLimit,
                })
              : `${tokensUsed.toLocaleString()} / ${tokenLimit.toLocaleString()}`}
          </Typography>
        </Box>
        {(isRateLimited || nextReset) && (
          <Typography variant="caption" color="text.secondary">
            {isRateLimited
              ? t("askMeAnything.dailyLimitReached")
              : t("askMeAnything.resets", { when: nextReset })}
          </Typography>
        )}
      </Box>
    );
  };

  const isEmpty = messages.length === 0 && !isTyping;

  const partnerLogos = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        mb: 3,
      }}
    >
      <img src={amhLogo} alt="AMH" style={{ height: 45 }} />
      <Typography color="text.secondary" sx={{ mx: 1 }}>
        ×
      </Typography>
      <img src={kijabeLogo} alt="Kijabe" style={{ height: 45 }} />
    </Box>
  );

  const messageInput = (
    <CustomMessageInput
      inputValue={inputValue}
      setInputValue={setInputValue}
      handleSendRequest={handleSend}
      // Dashboard users are unlimited — pass null userData so useRateLimit inside
      // CustomMessageInput sees no rate limit data and never disables the input.
      userData={isDashboard ? null : userData}
      disabled={!user}
      hasMessages={messages.length > 0}
    />
  );

  return (
    <Box
      sx={{
        p: 3,
        pb: 0,
        maxWidth: 1200,
        mx: "auto",
        // Fill available height as a flex column so the chat area can flex-grow
        height: isDashboard ? DASHBOARD_CONTENT_HEIGHT : "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Partner logos sit at the top once the chat has started; before that they're part
          of the centered welcome below. */}
      {!isEmpty && partnerLogos}

      {/* Usage bar — only for WP / unauthenticated path */}
      {!isDashboard && (
        <Box sx={{ maxWidth: 600, mx: "auto", width: "100%" }}>
          {renderUsageBar()}
        </Box>
      )}

      {!isDashboard && !user && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("askMeAnything.signInRequired")}
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isEmpty ? (
        // Welcome screen, like the other chats: logos, title, intro and the input, all
        // centered on the page until the first question is sent.
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 2,
            pb: 6,
          }}
        >
          {partnerLogos}
          <Typography variant="h4" sx={{ mb: 2, fontSize: { xs: "1.75rem", sm: "2.125rem" } }}>
            {t("askMeAnything.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, lineHeight: 1.6 }}>
            {t("askMeAnything.intro")}
          </Typography>
          <Box sx={{ width: "100%", maxWidth: 600, textAlign: "left" }}>{messageInput}</Box>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Message list */}
          <Box
            ref={chatBoxRef}
            sx={{
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              ...chatScrollbarSx,
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: "970px",
                display: "flex",
                flexDirection: "column",
                gap: 4,
                py: 2,
                px: 2,
              }}
            >
              {messages.map((msg, i) => (
                <ChatMessage
                  key={i}
                  role={msg.role === "user" ? "user" : "assistant"}
                  text={msg.content}
                  error={Boolean(msg.isError)}
                  copyable
                  footer={
                    msg.role === "assistant" && msg.sources?.length > 0 ? (
                      <SourceLinks sources={msg.sources} onOpenPdf={handleOpenPdf} />
                    ) : null
                  }
                />
              ))}

              {isTyping && <TypingIndicator />}
            </Box>
          </Box>

          <Box sx={{ mt: 2, pb: 1, px: 2, width: "100%", maxWidth: "970px", mx: "auto" }}>
            {messageInput}
          </Box>
        </Box>
      )}

      {/* PDF Viewer */}
      <PdfViewerDialog
        open={pdfViewer.open}
        url={pdfViewer.url}
        title={pdfViewer.title}
        onClose={() => setPdfViewer({ open: false, url: null, title: null })}
      />
    </Box>
  );
};

export default AskMeAnythingPage;
